/** @jsxImportSource @emotion/react */

import { FC, useCallback } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { isEqual } from 'lodash'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Chip from '@mui/material/Chip'
import { findAllForCMSTask_findAllForCMSTask_items as Task } from '__generatedTypes__/findAllForCMSTask'
import { SORTABLE_LIST_ANIMATION } from 'constants/consts'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import * as listClasses from 'pagesNew/StudySchedule/List/styles'
import { ReactComponent as EmptyListSvg } from 'components/QuestionAccordion/images/empty.svg'
import { ALL_SUBJECT_ID } from '../List/useTaskFilters'
import { TASK_TYPE_TITLE } from '../consts'
import * as classes from './styles'

type Props = {
  tasks: Task[]
  onEdit: (task: Task, index: number) => void
  onRemove: (index: number) => void
  onSort: (tasks: Task[]) => void
}

export const TasksList: FC<Props> = ({ tasks, onEdit, onRemove, onSort }) => {
  const sortedTasks = tasks.map((task) => ({
    id: task.id,
    task,
  }))

  const handleSort = useCallback(
    (sortedTasksNew: Array<{ id: string; task: Task }>) => {
      const newTasks = sortedTasksNew.map(({ task }) => task)

      if (isEqual(tasks, newTasks)) {
        return
      }

      onSort?.(newTasks)
    },
    [tasks]
  )

  if (!tasks.length) {
    return (
      <div css={[classes.listWrapper, classes.emptyList]}>
        <EmptyListSvg />
        <div>No tasks at the moment</div>
      </div>
    )
  }

  return (
    <div>
      <ReactSortable
        list={sortedTasks}
        setList={handleSort}
        animation={SORTABLE_LIST_ANIMATION}
        data-testid="tasks-list"
        css={classes.listWrapper}
      >
        {tasks.map((task, index) => (
          <div key={task.id} css={classes.listItem} data-testid={`task-${task.id}`}>
            <div data-testid="task-title">{task.title}</div>

            <div css={classes.actions}>
              <div>
                {task.subject ? (
                  <div css={listClasses.chipWrapper(task.subject.tabName)} data-testid="task-subject">
                    <Chip label={task.subject?.tabName} css={listClasses.subjectChip(task.subject.tabName)} />
                  </div>
                ) : (
                  <div css={listClasses.chipWrapper(ALL_SUBJECT_ID)}>
                    <Chip label="All" css={listClasses.subjectChip(ALL_SUBJECT_ID)} />
                  </div>
                )}
              </div>
              <div>
                <Chip label={TASK_TYPE_TITLE[task.type]} css={classes.chip} data-testid="task-type" />
              </div>
              <EditIcon
                css={[classes.icon, classes.iconEdit]}
                onClick={() => onEdit(task, index)}
                data-testid="task-edit-button"
              />
              <CloseRoundedIcon
                css={[classes.icon, classes.iconCross]}
                onClick={() => onRemove(index)}
                data-testid="task-delete-button"
              />
              <DnDIcon data-testid="task-dnd-icon" />
            </div>
          </div>
        ))}
      </ReactSortable>
    </div>
  )
}
